<script setup lang="ts">
import type { QueryFunctionContext } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { ref, toRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { refDebounced, syncRef } from '@vueuse/core'
import { useQueryClient } from '@tanstack/vue-query'
import { faMagnifyingGlass, faXmark } from '@fortawesome/duotone-regular-svg-icons'
import useAuthStore from '~/stores/auth'
import { useAppColor } from '~/composables/useAppColor'
import Button from '~/components/ui/Button.vue'

const props = defineProps<{
  title: string
  searchQuery: string
  placeholder: string
  queryKey: QueryFunctionContext['queryKey']
}>()

const emit = defineEmits(['updateSearchQuery'])

const { t } = useI18n()
const { themeColor } = useAppColor()
const queryClient = useQueryClient()
const { isAuthenticated } = storeToRefs(useAuthStore())

const searchInput = ref(props.searchQuery)
const debouncedSearch = refDebounced(searchInput, 700)

syncRef(toRef(props, 'searchQuery'), searchInput, { direction: 'ltr' })

watch(debouncedSearch, (query) => {
  queryClient.cancelQueries({ queryKey: props.queryKey })
  emit('updateSearchQuery', query)
})
</script>

<template>
  <div class="grid-in-search w-full space-y-3">
    <h1
      class="text-3xl font-extrabold text-violet-50"
      v-text="title"
    />
    <form
      role="search"
      class="relative"
      @submit.prevent="() => {}"
    >
      <font-awesome-icon
        :icon="faMagnifyingGlass"
        size="xl"
        class="absolute top-1/2 left-5 z-10 -translate-y-1/2 text-xl"
        :style="{ color: themeColor }"
      />
      <input
        v-model="searchInput"
        v-focus
        type="text"
        maxlength="200"
        name="search"
        :aria-label="t('search.title')"
        class="border-gray-10 w-full rounded-xl border bg-white p-3 pl-16 text-xl transition placeholder:text-lg placeholder:text-gray-50 focus-visible:ring"
        :placeholder="placeholder"
        :disabled="!isAuthenticated"
      >
      <Button
        v-if="searchInput.length"
        shape="square"
        size="sm"
        variant="ghost"
        class="absolute top-1/2 right-5 z-10 -translate-y-1/2"
        :disabled="!isAuthenticated"
        @click="emit('updateSearchQuery', '')"
      >
        <font-awesome-icon :icon="faXmark" />
      </Button>
    </form>
  </div>
</template>
